import React from 'react';
import Head from 'next/head';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import classNames from 'classnames';

import { bulkUpdate as navUpdateAction } from 'redux/modules/navbar';
import { globalLoadComplete } from 'redux/modules/router';
import { loadSlideshow } from 'redux/modules/slideshow';
import { setPageView, setVertical } from 'redux/modules/shared';

import {
  slideshow as slideshowPropType,
  socialMediaProfiles as socialProfilePropType,
} from 'lib/CustomPropTypes';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import LegacyIDService from 'lib/LegacyIDService';
import { navbar, NAVBAR_THEME } from 'lib/navbar';
import { getPathName } from 'lib/getPathName';
import { setAdLinkHeaders } from 'lib/setAdLinkHeaders';

import AdsBundle from 'components/AdsBundle';
import { ThemesCssBundle } from 'components/ThemesCssBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import ErrorBoundary from 'components/ErrorBoundary';
import { GlobalMetadata, SlideshowMetadata } from 'components/PageMetadata';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import SlideshowLede from 'components/SlideshowLede';
import SlideshowViewport from 'components/SlideshowViewport';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';

import ErrorPage from '../_error';

import 'assets/stylesheets/main.scss';
import 'assets/stylesheets/toolkit.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';


const pageView = 'slideshow';

const navbarConfig = {
  theme: () => NAVBAR_THEME.TRANSPARENT,
  headline: ({ content }) => get(content, ['headline'], null),
  activeAt: 100,
};

const mapStateToProps = ({ slideshow, shared }) => ({
  slideshow: slideshow.current,
  socialMediaProfiles: shared.socialMediaProfiles,
  vertical: shared.vertical,
});

class SlideshowPage extends React.Component {
  static async getInitialProps(ctx) {
    const {
      req: {
        params: {
          id: rawID,
        },
      },
      res: {
        locals: {
          domain,
          hostname,
          services,
          vertical,
        },
      },
      store,
      asPath,
    } = ctx;

    const id = LegacyIDService.slideshow(rawID);

    await Promise.all([
      store.dispatch(loadSlideshow(id)),
      store.dispatch(setPageView(pageView)),
      store.dispatch(setVertical(vertical)),
    ]);

    const slideshowStateAfterLoad = store.getState().slideshow;
    const statusCode = get(slideshowStateAfterLoad, 'error.status', 200);

    if (statusCode >= 400) {
      ctx.res.statusCode = statusCode;
    }

    // Populate props with HFS component data
    const layout = {};
    if (services && services.layout && services.layout.getComponent) {
      layout.header = await services.layout.getComponent(vertical, 'header');
      layout.footer = await services.layout.getComponent(vertical, 'footer');
    }

    const primaryUrl = get(slideshowStateAfterLoad, ['current', 'url', 'primary'], '');
    const headline = get(slideshowStateAfterLoad, ['current', 'headline'], {});

    setAdLinkHeaders(ctx.res, vertical);

    await Promise.all([
      store.dispatch(navUpdateAction({
        shareUrl: getPathName(primaryUrl),
        headline,
      })),

      store.dispatch(globalLoadComplete({
        domain,
        host: hostname,
        navigating: false,
        path: asPath,
      })),
    ]);

    return {
      layout,
      pageView,
      path: asPath,
      statusCode,
      vertical,
    };
  }

  static propTypes = {
    layout: servicesLayoutPropType.isRequired,
    slideshow: slideshowPropType.isRequired,
    socialMediaProfiles: socialProfilePropType.isRequired,
    vertical: PropTypes.string.isRequired,
    statusCode: PropTypes.number.isRequired,
  };

  handleMainContentError = () => {
    const { vertical } = this.props;
    return (
      <ErrorPage
        statusCode={500}
        vertical={vertical}
      />
    );
  };

  render() {
    const {
      slideshow,
      socialMediaProfiles,
      vertical,
      layout,
      statusCode,
    } = this.props;

    if (parseInt(statusCode, 10) >= 400) {
      return (
        <ErrorPage
          statusCode={statusCode === 404 ? 404 : 500}
          vertical={vertical}
          layout={layout}
        />
      );
    }

    const {
      headline,
      description,
      primaryImage,
      teaseImage,
      url,
      unibrow,
    } = slideshow;

    const mainImage = primaryImage || teaseImage;

    return (
      <>
        <Head>
          <IconfontStyleTag />
        </Head>
        <ThemesCssBundle vertical={vertical} />

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          <HeaderAndFooter layout={layout}>
            <ErrorBoundary errorHandler={this.handleMainContentError}>
              <ScrollingAnalytics contentType="slideshow" contentUrl={url.primary}>
                <section>
                  <GlobalMetadata />
                  <SlideshowMetadata slideshow={slideshow} vertical={vertical} />
                  <div className="slideshow">
                    <SlideshowLede
                      headline={headline}
                      dek={description.primary ? description.primary : ''}
                      unibrow={unibrow}
                      totalSlides={slideshow.totalSlides}
                      datePublished={slideshow.datePublished}
                      image={mainImage}
                    />
                    <SlideshowViewport
                      content={slideshow}
                      social={socialMediaProfiles}
                      vertical={vertical}
                    />
                  </div>
                </section>
              </ScrollingAnalytics>
            </ErrorBoundary>
          </HeaderAndFooter>
        </div>

        <AnalyticsLaunchScripts />
        <AdsBundle />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps)(
    SlideshowPage,
  ),
);
